// App.js — Site shell + routing + Configurable Tools Menu + Multi-Domain Access
import React, { useEffect, useMemo, useState } from "react";
import HubApp from "./HubApp";
import ABMGuide from "./ABMGuide"; // Marketing PDF
import SDRGuide from "./SDRGuide"; // SDR PDF
import ABMSalesPlaybook from "./ABMSalesPlaybook"; // Sales PDF
import ABMSocialCommentsPlaybook from "./ABMSocialCommentsPlaybook"; // Social Comments PDF
import SocialCommentsTool from "./SocialCommentsTool";
import CourseraSocialOutreach from "./CourseraSocialOutreach";
import SocialCommentsPlaybook from "./SocialCommentsPlaybook";
import PersonaCarousel from "./PersonaCarousel"; 
import TechPersonaCarousel from "./TechPersonaCarousel";

// =========================================================================
// 1. CONFIGURATION & PERMISSIONS
// =========================================================================

// Specific external emails (for freelancers/partners not on company domains)
const VIP_EMAILS = [
  "demo@test.com" 
];

// PERMISSION CHECKER
// Returns TRUE if email matches allowed domains OR the VIP list
const checkAccess = (email) => {
  const clean = (email || "").trim().toLowerCase();
  if (!clean) return false;
  
  return (
    clean.endsWith("@coursera.org") || 
    clean.endsWith("@fairwaydigitalmedia.com") || 
    VIP_EMAILS.includes(clean)
  );
};

// Tools Configuration
const TOOLS_CONFIG = [
  {
    slug: "social-comments",
    title: "Social Comments: Simple Cut & Paste",
    desc: "Turn social “reactions” into a clean contact table with exports.",
    type: "internal",
    restricted: false, 
    Component: SocialCommentsTool 
  },
  {
    slug: "social-comments-playbook", 
    title: "Social Playbook: Best Practices",
    desc: "Interactive guide: How to spot personas & turn comments into pipeline.",
    type: "internal",
    restricted: false,
    Component: SocialCommentsPlaybook 
  },
  {
    slug: "fs-persona-carousel", 
    title: "Financial Services: AI Skills Lens",
    desc: "Interactive persona guide for the 'Closing the Skills Gap' report.",
    type: "internal",
    restricted: false,
    Component: PersonaCarousel
  },
  {
    slug: "tech-persona-carousel",
    title: "Tech Software: Executive Lens",
    desc: "Interactive guide for Engineering, Product, and HR leaders in Tech.",
    type: "internal",
    restricted: false,
    Component: TechPersonaCarousel
  },
  {
    slug: "social-outreach",
    title: "Social Outreach: Manual-Assist",
    desc: "Upload a CSV → copy & open profiles → send your notes.",
    type: "internal",
    restricted: false,
    Component: CourseraSocialOutreach
  },
  {
    slug: "abm-planning-tool", // <--- NEW ANNUAL PLANNER
    title: "ABM Program Planner 2026",
    desc: "Interactive budget and program mix modeler for annual planning.",
    type: "external",
    restricted: true, // <--- HIDDEN UNLESS LOGGED IN
    href: "https://abmannualplanningtool.fairwaydigitalmedia.com/"
  },
  {
    slug: "forrester-prep",
    title: "Forrester Call Prep",
    desc: "Interactive guide for the analyst call (VIP Only).",
    type: "external",
    restricted: true, // <--- HIDDEN UNLESS LOGGED IN
    href: "https://forrestercallprep.fairwaydigitalmedia.com/"
  }
];

// ---------- Style tokens ----------
const CSS = `
  :root{
    --ink:#0B1A3C; --ink70:#0B1A3CB3;
    --line:#E6EDF2; --bg:#F4F6FA; --nav:#0B0E14;
    --c-blue:#2A73CC; --pill:#E9F2FF; --pill-ink:#0B3A84;
  }
  *{box-sizing:border-box}
  body{margin:0;background:var(--bg);color:var(--ink);
       font-family: "Source Sans Pro", system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif}

  /* Slim global black strip */
  .mastbar{height:28px;background:#0E0F13; display:flex; align-items:center; justify-content:flex-end; padding:0 16px;}
  .mastbar__login{ color:rgba(255,255,255,0.6); font-size:11px; text-decoration:none; cursor:pointer; font-weight:600; text-transform:uppercase; letter-spacing:0.5px;}
  .mastbar__login:hover{ color:white; }

  /* Main nav */
  .nav{position:sticky; top:0; z-index:60; background:#fff; border-bottom:1px solid var(--line)}
  .nav__in{max-width:1200px;margin:0 auto;display:flex;align-items:center;gap:18px;padding:10px 16px}
  .brandrow{display:flex;align-items:center;gap:10px;margin-right:auto}
  .brandrow img{height:18px}
  .fb{color:var(--ink70);font-weight:700;font-size:14px}

  .tabs{display:flex;gap:10px;align-items:center}
  .tab{display:inline-flex;align-items:center;gap:8px;padding:8px 12px;border-radius:999px;
       color:#183B76;font-weight:700;background:transparent;text-decoration:none;border:1px solid transparent; cursor:pointer;}
  .tab:hover{background:#F4F7FF}
  .tab--active{background:var(--pill); color:var(--pill-ink); border-color:#D5E6FF}
  .tab--home{padding:8px 10px;}

  /* Tools dropdown */
  .tools{position:relative}
  .tools__btn::after{content:"▾"; font-size:11px; margin-left:4px}
  .menu{position:absolute; top:100%; right:0; min-width:320px; background:#fff; border:1px solid var(--line);
        border-radius:10px; box-shadow:0 10px 30px rgba(0,0,0,.12); padding:6px; display:none; z-index:1000}
  .tools:hover .menu, .tools:focus-within .menu{display:block}
  
  /* Menu Items */
  .menu a{display:flex;align-items:start;gap:10px;padding:10px;border-radius:8px;color:var(--ink);text-decoration:none}
  .menu a:hover{background:#F7FAFF}
  .menu .k{font-size:12px;color:var(--ink70); margin-top:2px; line-height:1.4}
`;

/* ---------------- Routing helpers ---------------- */
function parseHash() {
  const raw = (window.location.hash || "").replace(/^#\/?/, "");
  const [head, sub] = raw.split("/");

  if (head === "marketing" || head === "sdr" || head === "sales") {
    return { route: "hub", tab: head };
  }
  if (head === "tools") {
    return { route: "tools", slug: (sub || "").toLowerCase() };
  }
  if (head === "pdf") {
    return { route: "pdf", slug: (sub || "marketing").toLowerCase() };
  }
  return { route: "hub", tab: "marketing" };
}

const LOGO_SVG = "https://pub-065b7688176a4969bec21707aed69fdc.r2.dev/coursera/logos/Coursera%20-%20Blue.svg";
const LOGO_PNG = "https://pub-065b7688176a4969bec21707aed69fdc.r2.dev/coursera/logos/Coursera%20-%20Blue.png";

/* ---------------- Top shell ---------------- */
function Shell({ children, activeTab, userEmail, onLogin, onLogout }) {
  const hasAccess = checkAccess(userEmail);

  return (
    <>
      <style>{CSS}</style>
      
      {/* Mastbar with Login/Logout */}
      <div className="mastbar">
        {userEmail ? (
          <div style={{display:'flex', gap:12, alignItems:'center'}}>
             <span style={{color:'#666', fontSize:11}}>
               {hasAccess ? "✅ Access Granted" : "⚠️ Guest Mode"} ({userEmail})
             </span>
             <span className="mastbar__login" onClick={onLogout}>Sign Out</span>
          </div>
        ) : (
          <span className="mastbar__login" onClick={onLogin}>🔒 Staff Login</span>
        )}
      </div>

      <nav className="nav" aria-label="ABM Hub navigation">
        <div className="nav__in">
          <div className="brandrow">
            <picture>
              <source srcSet={LOGO_SVG} type="image/svg+xml" />
              <img src={LOGO_PNG} alt="Coursera" />
            </picture>
            <span className="fb">for Business</span>
          </div>

          <div className="tabs" role="tablist">
            
            {/* 1. HOME BUTTON */}
            <a 
              className={`tab tab--home ${activeTab === "marketing" ? "tab--active" : ""}`} 
              href="#marketing"
              title="Home"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{opacity:0.8}}>
                <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
              </svg>
            </a>

            {/* 2. MAIN TABS */}
            <a className={`tab ${activeTab === "marketing" ? "tab--active" : ""}`} href="#marketing">ABM Marketing</a>
            <a className={`tab ${activeTab === "sdr" ? "tab--active" : ""}`} href="#sdr">ABM SDR</a>
            <a className={`tab ${activeTab === "sales" ? "tab--active" : ""}`} href="#sales">ABM Sales</a>

            {/* 3. TOOLS DROPDOWN */}
            <div className="tools">
              <a
                className={`tab tools__btn ${activeTab === "tools" ? "tab--active" : ""}`}
                href="#/tools"
                onClick={(e) => e.preventDefault()}
              >
                ABM Tools
              </a>
              <div className="menu" role="menu">
                {TOOLS_CONFIG.filter(t => !t.restricted || hasAccess).map((tool) => {
                  const isExt = tool.type === "external";
                  return (
                    <a 
                      key={tool.slug} 
                      href={isExt ? tool.href : `#/tools/${tool.slug}`}
                      target={isExt ? "_blank" : "_self"}
                      rel={isExt ? "noopener noreferrer" : ""}
                    >
                      <div>
                        <div style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
                          {tool.title}
                          {isExt && <span style={{fontSize: 10, opacity: 0.5}}>↗</span>}
                          {tool.restricted && <span style={{fontSize:10}}>🔒</span>}
                        </div>
                        <div className="k">{tool.desc}</div>
                      </div>
                    </a>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </nav>
      {children}
    </>
  );
}

/* ---------------- Router ---------------- */
export default function App() {
  const [state, setState] = useState(parseHash());
  const [userEmail, setUserEmail] = useState("");

  // 1. Load user from local storage
  useEffect(() => {
    const stored = localStorage.getItem("abm_user_email");
    if (stored) setUserEmail(stored);
  }, []);

  // 2. Login Handler
  const handleLogin = () => {
    const email = window.prompt("Enter your Coursera or Fairway email:");
    if (email) {
      const lower = email.trim().toLowerCase();
      // Basic validation
      if (lower.includes("@")) {
        localStorage.setItem("abm_user_email", lower);
        setUserEmail(lower);
        window.location.reload(); 
      } else {
        alert("Please enter a valid email address.");
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("abm_user_email");
    setUserEmail("");
    window.location.reload();
  };

  useEffect(() => {
    const onHash = () => setState(parseHash());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  // Default redirect
  useEffect(() => {
    if (!window.location.hash) {
      window.location.hash = "marketing";
    }
  }, []);

  const activeTab = useMemo(() => {
    if (state.route === "hub") return state.tab || "marketing";
    if (state.route === "tools") return "tools";
    if (state.route === "pdf") {
      return state.slug === "sdr" ? "sdr" : state.slug === "sales" ? "sales" : "marketing";
    }
    return "marketing";
  }, [state]);

  let page = <HubApp />;

  if (state.route === "hub") {
    page = <HubApp />;
  } 
  else if (state.route === "tools") {
    const found = TOOLS_CONFIG.find(t => t.slug === state.slug && t.type === "internal");
    
    // Security check
    const hasAccess = checkAccess(userEmail);
    if (found && found.restricted && !hasAccess) {
       alert("🔒 Restricted Access: Please log in with a Coursera or Fairway email.");
       window.location.hash = "marketing"; 
       page = <HubApp />;
    } else if (found && found.Component) {
      const ToolComponent = found.Component;
      page = <ToolComponent />;
    } else {
      page = <HubApp />; 
    }
  } 
  else if (state.route === "pdf") {
    switch ((state.slug || "").toLowerCase()) {
      case "sdr": page = <SDRGuide compact />; break;
      case "sales": page = <ABMSalesPlaybook compact />; break;
      case "tool-social": 
      case "social-comments": page = <ABMSocialCommentsPlaybook compact />; break;
      case "marketing": default: page = <ABMGuide compact />; break;
    }
  }

  if (!page) page = <HubApp />;
  if (state.route === "pdf") return page;
  
  return (
    <Shell 
      activeTab={activeTab} 
      userEmail={userEmail} 
      onLogin={handleLogin} 
      onLogout={handleLogout}
    >
      {page}
    </Shell>
  );
}
